/**
 * autocomplete.js — Algolia Autocomplete
 *
 * Custom autocomplete experience using @algolia/autocomplete-js.
 * Handles the frontend search UI only. Index creation and product sync are
 * managed separately via the Algolia Shopify app (backend-only mode) —
 * we are NOT using the app's native frontend widgets.
 *
 * Decision log:
 * - Custom frontend over Algolia's native Shopify app UI: full design control
 *   across all three brand storefronts
 * - Carousel outside source system: direct fetch + Splide carousel avoids
 *   conflict with autocomplete-js's .aa-List/.aa-Item wrapper markup
 * - config.onOpen pattern: brand-specific panel content (bestsellers, featured
 *   collections, etc.) defined in _config.autocomplete.js, not hardcoded here; brands with
 *   nothing to pre-fetch simply omit onOpen
 * - Debouncing here through tiny helper function to avoid adding lodash dependency. 
 *   Not debouncing via characters for suggestions, only typing delay.
 * - lite over algoliasearch — no non-lite features needed
 */

import { render, h } from 'preact';
import { mountSplide } from 'mondetta';
import { productCardTemplate } from '../snippets/productCardTemplate';
import { collectionCardTemplate } from '../snippets/collectionCardTemplate';
import { pageCardTemplate } from '../snippets/pageCardTemplate';
import { querySuggestionTemplate } from '../snippets/querySuggestionTemplate';
import { algoliaConfig } from '../_config';
import { getBrand } from '../_getBrand';
import { autocomplete } from '@algolia/autocomplete-js';
import { liteClient as algoliasearch } from 'algoliasearch/lite';
import { applyMarketPricing } from '../_marketPricing';

/**
 * Module-scoped — persists across renders.
 * initAutocomplete() is only ever called once, so no reset needed.
 */
let currentHitsSignature = '';
let defaultHits = [];
let currentHits = [];
let fetched = false;

const brand = getBrand();
const config = algoliaConfig[brand];
const brandDebounceMs = config.autocomplete.debounceMs;
const brandDebounceChars = config.autocomplete.debounceChars;

/**
 * createDebounced()
 *
 * Returns a tiny debouncer factory that delays execution of the provided
 * function and resolves with its result. Create one debouncer per source
 * so timers don't collide between sources.
 */
function createDebounced(wait = brandDebounceMs, minChars = brandDebounceChars) {
  let timer = null;

  return (fn) =>
    new Promise((resolve, reject) => {
      if (timer) clearTimeout(timer);

      timer = setTimeout(() => {
        Promise.resolve()
          .then(fn)
          .then(resolve, reject)
          .finally(() => {
            timer = null;
          });
      }, wait);

    });
}

/**
 * autocompleteCardTemplate()
 *
 * Switches item/hit template depending on the index it came from
 */
function autocompleteCardTemplate(hit) {
  switch (hit.__indexName) {
    case 'shopify_products':
      return productCardTemplate(hit);
    case 'shopify_collections':
      return collectionCardTemplate(hit);
    case 'shopify_pages':
      return pageCardTemplate(hit);
    default:
      return '';
  }
}

/**
 * mountAutocompleteCarousel()
 *
 * Builds and mounts a Splide carousel into #autocompleteCarousel using
 * pre-fetched hits. Called after config.onOpen() resolves on first panel open.
 *
 * Rendered outside the autocomplete source system to retain full DOM control —
 * autocomplete-js wraps source items in .aa-List/.aa-Item markup that conflicts
 * with Splide's required structure.
 */
function mountAutocompleteCarousel() {
  // Prefer the Products column Splide container, fall back to the
  // bestsellers container used on initial open/idle state.
  const acPanelElement = document.querySelector('.ac-panel');
  const productEl = document.querySelector('.ac-results__products #autocompleteCarouselProducts');
  const autocompleteCarouselInitialEl = document.querySelector('#autocompleteCarouselInitial');
  const el = productEl || autocompleteCarouselInitialEl;

  if (!el || !currentHits.length) return; // bail out if no results

  const signature = currentHits.map(h => h.objectID).join(',');
  if (el.classList.contains('is-initialized') && signature === currentHitsSignature) return;

  // Destroy and reset any other Splide instances or remnants to avoid
  // leftover arrows/controls in non-product areas. We target any element
  // carrying Splide markup or attributes and remove/mask them before
  // we compute the target track so children aren't accidentally moved.
  acPanelElement.querySelectorAll('[data-splide]').forEach(node => {
    if (node === el) return;
    const splideEl = node;
    if (splideEl.splideInstance) {
      try { splideEl.splideInstance.destroy(); } catch (e) { /* ignore */ }
      splideEl.splideInstance = null;
    }
    splideEl.querySelectorAll('.splide__arrows').forEach(a => a.remove());
    const trackEl = splideEl.querySelector('.splide__track');
    if (trackEl) {
      trackEl.innerHTML = '<ul class="splide__list"></ul>';
    } else {
      splideEl.innerHTML = '<div class="splide__track"><ul class="splide__list"></ul></div>';
    }
    splideEl.removeAttribute('data-splide');
    splideEl.classList.remove(
      'is-initialized',
      'is-active',
      'is-overflow',
      'splide--slide',
      'splide--ltr',
      'splide--draggable'
    );
  });

  // Also remove any stray arrows or track elements that aren't attached to
  // a proper Splide container (sometimes arrows are injected into wrapper
  // nodes). This helps prevent collections or other sources from being
  // visually wrapped by leftover controls.
  acPanelElement.querySelectorAll('.splide__arrows, .splide__track').forEach(node => {
    if (node.closest('[data-splide]') === el || node.closest('.splide') === el) return;
    node.remove();
  });

  // Recompute the track after cleanup to ensure we target the correct list
  // element and that no other container has stolen the children.
  const track = el.querySelector('.splide__list');
  if (!track) {
    // Ensure minimal structure exists if removed previously
    el.innerHTML = '<div class="splide__track"><ul class="splide__list"></ul></div>';
  }
  const finalTrack = el.querySelector('.splide__list');
  if (!finalTrack) return;

  // Continue with mounting for the selected element
  currentHitsSignature = signature;

  // Options set via JS — autocomplete's htm renderer mangles data-splide attribute values
  el.setAttribute('data-splide', JSON.stringify({
    type: 'slide',
    dragMinThreshold: 50,
    pagination: false,
    fixedWidth: '9rem',
    perMove: 2, // TODO: Custom desktop vs mobile?
    rewind: false, // no duplicate slides = faster load 
    autoplay: false,
    omitEnd: true, // prevent double clicking arrows
    focus: 1, // required by omitEnd
    autoplay: false, // for some reason it autoplays without this here
  }));

  // Populate the track with slides
  finalTrack.innerHTML = '';
  currentHits.forEach(hit => {
    const li = document.createElement('li');
    li.className = 'splide__slide';
    render(autocompleteCardTemplate(hit), li);
    finalTrack.appendChild(li);
  });

  mountSplide(el);

  // Final pass: remove any stray arrows or Splide attributes outside the
  // intended mount element — sometimes Splide or the renderer will inject
  // controls into sibling/wrapper nodes after mounting.
  acPanelElement.querySelectorAll('.splide__arrows, [data-splide], .splide').forEach(node => {
    if (node === el || node.closest('#' + el.id) === el || el.contains(node)) return;
    // Remove only the arrows and data attributes; leave other content alone
    if (node.classList && node.classList.contains('splide__arrows')) {
      node.remove();
    } else if (node.hasAttribute && node.hasAttribute('data-splide')) {
      node.removeAttribute('data-splide');
    } else if (node.classList && node.classList.contains('splide')) {
      node.classList.remove('splide');
    }
  });
}


/**
 * initAutocomplete()
 *
 * Mounts the Algolia Autocomplete instance onto #algoliaHeaderAutocomplete.
 * Call on DOMContentLoaded, guarded by a container existence check (see mondetta.js).
 */
export function initAutocomplete() {
  const brand = getBrand();
  const config = algoliaConfig[brand];

  const searchClient = algoliasearch(
    config.algolia.appId,
    config.algolia.apiKey
  );

  // Per-source debouncers to avoid cross-source timer collisions.
  const debouncedSuggestions = createDebounced(brandDebounceMs);
  const debouncedProducts = createDebounced(brandDebounceMs);
  const debouncedCollectionsPages = createDebounced(brandDebounceMs);


  autocomplete({
    container: '#algoliaHeaderAutocomplete',
    placeholder: '', // empty: no visible placeholder (+accessibility +UX)
    panelPlacement: 'full-width',
    detachedMediaQuery: '', // always detached mode (button + modal)
    openOnFocus: true, // shows marketing content before user types

    // call mount from the render cycle so it's always a fresh DOM
    render(params, root) {
      config.render.autocomplete(params, root);
      setTimeout(mountAutocompleteCarousel, 0);
    },

    // Fetch and mount brand-specific panel content on first open.
    // config.onOpen is optional — brands with nothing to pre-fetch simply omit it.
    onStateChange({ state }) {
      if (state.isOpen && !fetched) {
        fetched = true;
        config.onOpen?.(searchClient, config)
          .then(hits => applyMarketPricing(hits))
          .then(hits => {
            defaultHits = hits;
            currentHits = hits.map(hit => ({
              ...hit,
              __indexName: 'shopify_products',
            }));
            mountAutocompleteCarousel();
          })
          .catch(err => console.error('[Autocomplete] onOpen failed:', err));
      }
    },

    // Fetch the brand-specific submit instructions
    onSubmit({ state }) {
      if (!state.query) return;
      window.location.href = `/search?q=${encodeURIComponent(state.query)}`;
    },

    getSources({ query }) {
      const sources = [];

      // if suggestions are enabled
      if (config.autocomplete.suggestions.enabled) {
        sources.push({
          sourceId: 'querySuggestions',
          getItemUrl({ item }) {
            return `/search?q=${encodeURIComponent(item.query)}`;
          },
          getItems() {
            return debouncedSuggestions(() =>
              searchClient.search({
                requests: [{
                  indexName: 'query_suggestions',
                  query,
                  hitsPerPage: config.autocomplete.suggestions.hitsPerPage,
                  highlightPreTag: '__aa-highlight__',
                  highlightPostTag: '__/aa-highlight__',
                }]
              }).then(({ results }) => results[0].hits)
            );
          },
          templates: {
            header({ state }) {
              return state.query
                ? config.autocomplete.suggestions.headerTextResults
                : config.autocomplete.suggestions.headerText;
            },

            item: querySuggestionTemplate,
          },
        });
      }



      // if carousel results (products) are enabled
      if (config.autocomplete.carouselResults.enabled) {
        sources.push({
          sourceId: 'products',
          getItems({ setContext }) {
            if (query.length < brandDebounceChars) {
              return [];
            }

            return debouncedProducts(() =>
              searchClient.search({
                requests: [{
                  indexName: 'shopify_products',
                  query,
                  hitsPerPage: config.autocomplete.productHitsPerPage,
                }],
              }).then(async ({ results }) => {
                // Stash total hit count so templates.footer can read it via
                // state.context. Must be set before returning hits so it's
                // available on the same render cycle.
                setContext({ productsNbHits: results[0].nbHits });
                const hits = results[0].hits.map(hit => ({
                  ...hit,
                  __indexName: results[0].index,
                }));
                await applyMarketPricing(hits);
                // Keep module-scoped products in sync so the Splide carousel
                // can be populated from the latest search results.
                currentHits = hits;
                return hits;
              })
            );
          },
          templates: {
            item({ item }) {
              return autocompleteCardTemplate(item);
            },
          },
          onSelect(params) {
            console.log('selected', params);
          }
        });
      }





      // if collection and page results are enabled
      if (config.autocomplete.collectionPageResults.enabled) {
        sources.push({
          sourceId: 'collectionsAndPages',
          getItems({ setContext }) {
            if (query.length < brandDebounceChars) {
              return [];
            }

            return debouncedCollectionsPages(() =>
              searchClient.search({
                requests: [
                  {
                    indexName: 'shopify_collections',
                    query,
                    hitsPerPage: config.autocomplete.collectionPageResults.hitsPerPage,
                  },
                  {
                    indexName: 'shopify_pages',
                    query,
                    hitsPerPage: config.autocomplete.collectionPageResults.hitsPerPage,
                  },
                ],
              }).then(({ results }) => {
                // Combined nbHits across both indexes — stashed in context so
                // templates.footer can surface the total without re-fetching.
                setContext({ collectionsAndPagesNbHits: results[0].nbHits + results[1].nbHits });

                const collections = results[0].hits.map(hit => ({ ...hit, __indexName: results[0].index }));
                const pages = results[1].hits.map(hit => ({ ...hit, __indexName: results[1].index }));

                const interleaved = [];
                const maxLen = Math.max(collections.length, pages.length);
                for (let i = 0; i < maxLen; i++) {
                  if (collections[i]) interleaved.push(collections[i]);
                  if (pages[i]) interleaved.push(pages[i]);
                }

                return interleaved.slice(0, config.autocomplete.collectionPageResults.hitsPerPage);
              })
            );
          },
          templates: {
            header() {
              return config.autocomplete.collectionPageResults.headerText;
            },
            item({ item }) {
              return autocompleteCardTemplate(item);
            },
            footer({ state }) {
              const nbHits = state.context.collectionsAndPagesNbHits ? state.context.collectionsAndPagesNbHits : "";
              // Only show if there are more results than the panel cap.
              // Prevents a redundant "See all 3 results" when all 3 are visible.
              if (!state.query || !nbHits || nbHits <= config.autocomplete.collectionPageResults.hitsPerPage) return null;
              return h('a', {
                // jump to bottom of results (#) to see pages/collections results more easily
                href: `/search?q=${encodeURIComponent(state.query)}#algoliaSRPCollectionsPages`,
                class: 'link ac-see-all ac-see-all--collections-pages',
              }, `See all ${nbHits} pages`);
            },
            noResults() {
              return config.autocomplete.collectionPageResults.noResultsText;
            },
          },
          onSelect(params) {
            console.log('selected', params);
          }
        });
      }

      return sources;
    }
  });
}